import React from "react";
import { Storage } from "aws-amplify";
import styled, { css } from "styled-components";

import { fileToString } from "src/library/utils";

import { Text } from "../Text";
import { IFormImageInput } from ".";

export interface StyledInputImageProps {
  file: string | undefined;
  imageHeight?: number;
  imageWidth?: number;
  border?: string;
}

/**
 * Input image
 */
const InputImage: React.ReactFCWithChildren<IFormImageInput> = ({
  name, value, height, width, border, handleChange
}) => {
  const [ file, setFile ] = React.useState<string>();

  // On value change, get the file's data to output
  React.useEffect(() => {
    // If a value exists, update the file state
    if (value) {
      // If the value is a string assume that it is a S3 URL and load it from
      // Amplify's storage class. Otherwise covert the file to a data URL string.
      if (typeof value === "string") {
        Storage.get(value, { level: "public" }).then(data => setFile(data.toString()));
      } else {
        fileToString(value)
          .then(dataUrl => setFile(dataUrl))
          .catch(err => console.error(err));
      }
    } else {
      setFile(undefined);
    }
  }, [ value ]);

  return (
    <StyledInputImage
      file={file}
      imageHeight={height}
      imageWidth={width}
      border={border}
    >
      <input
        id={name}
        name={name}
        type="file"
        onChange={handleChange}
        className="form-control"
        accept="image/png, image/jpeg"
      />

      {!file ? (
        <>
          <button
            className="btn"
            type="button"
          >
            <Text
              fontSize="p"
              color="white"
            >
              Drop a file or 'click' to upload
            </Text>
          </button>
        </>
      ) : (
        <img
          src={file}
          alt={name}
        />
      )}
    </StyledInputImage>
  );
};

export default InputImage;

const StyledInputImage = styled.div<StyledInputImageProps>`
  ${({
    theme, file, imageHeight, imageWidth, border
  }) => css`
    transition: ${theme.transition.quickest};
    position: relative;
    display: inline-block;
    width: ${imageWidth ? `${imageWidth}px` : "30rem"};
    height: ${imageHeight ? `${imageHeight}px` : "20rem"};
    background-color: ${theme.colors.primaryLight};
    border: ${border ? border : `0.1rem solid ${theme.colors.primaryLight}`};
    ${file &&
      css`
        background-image: url(${file});
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
      `}

    &:hover {
      background-color: ${theme.colors.primaryDark};
    }

    .btn {
      position: absolute;
      width: 100%;
      height: 100%;
      color: ${theme.colors.white};
      background-color: transparent;
      border: none;
      outline: none;
    }

    input[type="file"] {
      opacity: 0;
      position: absolute;
      z-index: 10;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
    }

    img {
      display: block;
      position: absolute;
      z-index: 2;
      top: 0;
      left: 0;
      width: 100%;
      height: auto;
    }
  `};
`;
